/* eslint-disable @typescript-eslint/no-invalid-void-type */

import { z } from "zod";

import { apiSlice } from "api/api.slice";
import type {
  IdArg,
  LiveBriefingSampleResponse,
  SearchTopicsResponse,
  SimilarDimensionArg,
  Topic,
  TopicMetadataResult,
} from "api/api.types";
import { QUERY_LIMIT } from "api/constants";
import { liveBriefingSampleResponseSchema } from "api/schemas/briefingsApiSchema";
import { searchTopicsResponseSchema, topicMetadataResultSchema, topicSchema } from "api/schemas/topicsApiSchema";
import { toSearchString } from "api/utils";
import type { TopicVisibilityPatch } from "domains/topicshare/topicshare.types";
import type { DestinationTypes } from "domains/types";
import {
  type CustomMapOwner,
  followTopic,
  followTopicFailure,
  followTopicSuccess,
  unfollowTopic,
  unfollowTopicFailure,
  unfollowTopicSuccess,
} from "entities/topics/actions";
import type { RootState } from "types/redux";

type TopicCuratedArg = {
  search: { canEdit?: boolean; limit: number; offset: number; owner?: CustomMapOwner; type?: DestinationTypes };
};

type PatchTopicVisibilityArg = { body: TopicVisibilityPatch; topic_id: string };

type DeleteTopicAccessArg = { topic_id: string };

type DeleteTopicArg = { topic_id: string };

export const topicsApiSlice = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    getCustomMaps: build.infiniteQuery<Topic[], unknown, number>({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
          if (lastPage.length < QUERY_LIMIT) return undefined;

          return lastPageParam + 1;
        },
      },
      providesTags: ["CustomMaps"],
      query: ({ pageParam }) => {
        const offset = (pageParam - 1) * QUERY_LIMIT;

        return {
          url: "/topic/curated",
          params: {
            limit: QUERY_LIMIT,
            offset,
            types: "Custom Maps",
            can_edit: true,
            owner: true,
          },
          schema: z.array(topicSchema),
        };
      },
    }),
    getTopic: build.query<Topic, IdArg>({
      query: ({ id }) => ({ url: `/topic/${id}`, schema: topicSchema }),
    }),
    getTopicCurated: build.query<Topic[], TopicCuratedArg>({
      query: ({ search }) => ({
        url: `/topic/curated${toSearchString(search)}`,
        schema: z.array(topicSchema),
      }),
    }),
    getTopicMetadata: build.query<TopicMetadataResult, void>({
      query: () => ({ url: "/topic/metadata", schema: topicMetadataResultSchema }),
    }),
    getTopics: build.infiniteQuery<Topic[], void, number>({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
          if (lastPage.length < QUERY_LIMIT) return undefined;

          return lastPageParam + 1;
        },
      },
      query: ({ pageParam }) => {
        const offset = (pageParam - 1) * QUERY_LIMIT;

        return {
          url: "/topic/curated",
          params: {
            limit: QUERY_LIMIT,
            offset,
            types: "All Topics",
            can_edit: false,
          },
          schema: z.array(topicSchema),
        };
      },
    }),
    getSimilarTopicsByDimension: build.query<Topic[], SimilarDimensionArg>({
      query: ({ body, search }) => ({
        body,
        method: "POST",
        url: `/topic/similar/dimension${toSearchString(search)}`,
        schema: z.array(topicSchema),
      }),
    }),
    patchTopicVisibility: build.mutation<unknown, PatchTopicVisibilityArg>({
      query: ({ body, topic_id }) => ({ body, method: "PATCH", url: `/topic/${topic_id}/visibility` }),
    }),
    deleteTopicAccess: build.query<unknown, DeleteTopicAccessArg>({
      query: ({ topic_id }) => ({ method: "DELETE", url: `/topic/${topic_id}/access` }),
    }),
    deleteTopic: build.mutation<unknown, DeleteTopicArg>({
      query: ({ topic_id }) => ({ method: "DELETE", url: `/topic/${topic_id}` }),
      onQueryStarted: async ({ topic_id }, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          topicsApiSlice.util.updateQueryData("getCustomMaps", undefined, (draft) => {
            draft.pages.forEach((page) => {
              const index = page.findIndex((topic) => topic.id === topic_id);
              if (index !== -1) page.splice(index, 1);
            });
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: [{ type: "CustomMaps" }],
    }),
    followTopic: build.mutation<unknown, { id: string }>({
      query: ({ id }) => ({ method: "PUT", url: `/topic/${id}/follow` }),
      // eslint-disable-next-line @typescript-eslint/unbound-method -- this is the documented way to use getState inside onQueryStarted
      async onQueryStarted({ id }, { dispatch, queryFulfilled, getState }) {
        const state = getState();
        dispatch(followTopic({ id }));
        const customMapsOptimisticUpdate = dispatch(
          topicsApiSlice.util.updateQueryData("getCustomMaps", undefined, (draft) => {
            draft.pages.forEach((page) => {
              const followedTopic = page.find((topic) => topic.id === id);
              if (followedTopic) followedTopic.is_following = true;
            });
          }),
        );
        const getTopicsOptimisticUpdate = dispatch(
          topicsApiSlice.util.updateQueryData("getTopics", undefined, (draft) => {
            draft.pages.forEach((page) => {
              const followedTopic = page.find((topic) => topic.id === id);
              if (followedTopic) followedTopic.is_following = true;
            });
          }),
        );
        const currentSearchQuery = (state as RootState).search.currentSearchQuery;
        const searchTopicsOptimisticUpdate = dispatch(
          topicsApiSlice.util.updateQueryData("searchTopics", { search: currentSearchQuery }, (draft) => {
            draft.pages.forEach((page) => {
              const followedTopic = page.find((topic) => topic.id === id);
              if (followedTopic) followedTopic.is_following = true;
            });
          }),
        );
        try {
          await queryFulfilled;
          dispatch(followTopicSuccess({ id, isFollowing: true }));
        } catch {
          customMapsOptimisticUpdate.undo();
          getTopicsOptimisticUpdate.undo();
          searchTopicsOptimisticUpdate.undo();
          dispatch(followTopicFailure({ id }, { logoutOnAuthError: true }));
        }
      },
      invalidatesTags: [{ type: "CustomMaps" }],
    }),
    unfollowTopic: build.mutation<unknown, { id: string }>({
      query: ({ id }) => ({ method: "DELETE", url: `/topic/${id}/follow` }),
      // eslint-disable-next-line @typescript-eslint/unbound-method -- this is the documented way to use getState inside onQueryStarted
      async onQueryStarted({ id }, { dispatch, queryFulfilled, getState }) {
        const state = getState();
        dispatch(unfollowTopic({ id }));
        const customMapsOptimisticUpdate = dispatch(
          topicsApiSlice.util.updateQueryData("getCustomMaps", undefined, (draft) => {
            draft.pages.forEach((page) => {
              const followedTopic = page.find((topic) => topic.id === id);
              if (followedTopic) followedTopic.is_following = false;
            });
          }),
        );
        const getTopicsOptimisticUpdate = dispatch(
          topicsApiSlice.util.updateQueryData("getTopics", undefined, (draft) => {
            draft.pages.forEach((page) => {
              const followedTopic = page.find((topic) => topic.id === id);
              if (followedTopic) followedTopic.is_following = false;
            });
          }),
        );
        const currentSearchQuery = (state as RootState).search.currentSearchQuery;
        const searchTopicsOptimisticUpdate = dispatch(
          topicsApiSlice.util.updateQueryData("searchTopics", { search: currentSearchQuery }, (draft) => {
            draft.pages.forEach((page) => {
              const followedTopic = page.find((topic) => topic.id === id);
              if (followedTopic) followedTopic.is_following = false;
            });
          }),
        );
        try {
          await queryFulfilled;
          dispatch(unfollowTopicSuccess({ id, isFollowing: false }));
        } catch {
          customMapsOptimisticUpdate.undo();
          getTopicsOptimisticUpdate.undo();
          searchTopicsOptimisticUpdate.undo();
          dispatch(unfollowTopicFailure({ id }, { logoutOnAuthError: true }));
        }
      },
      invalidatesTags: [{ type: "CustomMaps" }],
    }),
    liveBriefingSample: build.query<LiveBriefingSampleResponse, void>({
      query: () => ({ url: "/topic/live_briefing_sample", schema: liveBriefingSampleResponseSchema }),
    }),
    searchTopics: build.infiniteQuery<SearchTopicsResponse, { search?: string; mapType?: "Forum" | "Custom" }, number>({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
          if (lastPage.length < QUERY_LIMIT) return undefined;

          return lastPageParam + 1;
        },
      },
      query: ({ queryArg, pageParam }) => {
        const offset = (pageParam - 1) * QUERY_LIMIT;

        return {
          method: "POST",
          url: "/topic/similar/text",
          body: {
            data: { text: queryArg.search ?? "" },
            similarity_mode: "hybrid",
            map_type: queryArg.mapType,
          },
          params: {
            limit: QUERY_LIMIT,
            offset,
            types: "All Topics",
            can_edit: false,
          },
          schema: searchTopicsResponseSchema,
        };
      },
    }),
  }),
});

export const {
  useGetCustomMapsInfiniteQuery,
  useGetTopicQuery,
  useLiveBriefingSampleQuery,
  usePatchTopicVisibilityMutation,
  useDeleteTopicMutation,
  useFollowTopicMutation,
  useUnfollowTopicMutation,
  useLazyDeleteTopicAccessQuery,
  useLazyGetSimilarTopicsByDimensionQuery,
  useLazyGetTopicMetadataQuery,
} = topicsApiSlice;
